import { isPresent } from '@ais-ui/utils';
import type {
  ReceivedMessage,
  Schedule,
  SentMessage,
} from '@ais-ui/api-spec/config';
import {
  ReceivedMessageWithDate,
  ScheduleWithDate,
  SentMessageWithDate,
} from '@ais-ui/api';

const filterReceivedMessage = (
  message: ReceivedMessageWithDate,
  name: string
): ReceivedMessageWithDate | null => {
  const lowercaseName = name.toLowerCase();

  // Check if the text field of the message includes the name
  const textMatch = message.text?.toLowerCase().includes(lowercaseName);

  // Check if the name field in the source detail of the message includes the name
  const sourceNameMatch = message.source?.name
    ?.toLowerCase()
    .includes(lowercaseName);

  // Check if the mmsi field in the source detail of the message includes the name
  const sourceMmsiMatch = message.source?.mmsi
    ?.toString()
    .includes(lowercaseName);

  // Check if the imo field in the source detail of the message includes the name
  const sourceImoMatch = message.source?.imo
    ?.toString()
    .includes(lowercaseName);

  // Check if the callsign field in the source detail of the message includes the name
  const sourceCallsignMatch = message.source?.callsign
    ?.toLowerCase()
    .includes(lowercaseName);

  const sourceMatch =
    sourceNameMatch || sourceMmsiMatch || sourceImoMatch || sourceCallsignMatch;

  // Check if the name field in the destination detail of the message includes the name
  const destinationNameMatch = message.destination?.name
    ?.toLowerCase()
    .includes(lowercaseName);

  // Check if the mmsi field in the source detail of the message includes the name
  const destinationMmsiMatch = message.destination?.mmsi
    ?.toString()
    .includes(lowercaseName);

  // Check if the imo field in the source detail of the message includes the name
  const destinationImoMatch = message.destination?.imo
    ?.toString()
    .includes(lowercaseName);

  // Check if the callsign field in the source detail of the message includes the name
  const destinationCallsignMatch = message.destination?.callsign
    ?.toLowerCase()
    .includes(lowercaseName);

  const destinationMatch =
    destinationNameMatch ||
    destinationMmsiMatch ||
    destinationImoMatch ||
    destinationCallsignMatch;

  if (textMatch || sourceMatch || destinationMatch) {
    return message;
  }

  return null;
};

export const filterReceivedMessages = (
  messages: ReceivedMessageWithDate[],
  name: string
) => {
  const lowercaseName = name.toLowerCase();
  return messages
    .map((message) => filterReceivedMessage(message, lowercaseName))
    .filter(isPresent);
};

const filterSentMessage = (
  message: SentMessageWithDate,
  name: string
): SentMessageWithDate | null => {
  const lowercaseName = name.toLowerCase();

  // Check if the text field of the message includes the name
  const textMatch = message.text?.toLowerCase().includes(lowercaseName);

  // Check if the mmsi field in the source detail of the message includes the name
  const sourceMmsiMatch = message.source?.mmsi
    ?.toString()
    .includes(lowercaseName);

  // Check if the mmsi field in the source detail of the message includes the name
  const destinationMmsiMatch = message.destination?.mmsi
    ?.toString()
    .includes(lowercaseName);

  if (textMatch || sourceMmsiMatch || destinationMmsiMatch) {
    return message;
  }

  return null;
};

export const filterSentMessages = (
  messages: SentMessageWithDate[],
  name: string
) => {
  const lowercaseName = name.toLowerCase();
  return messages
    .map((message) => filterSentMessage(message, lowercaseName))
    .filter(isPresent);
};

const filterMessageSchedule = (
  schedule: ScheduleWithDate,
  name: string
): ScheduleWithDate | null => {
  const lowercaseName = name.toLowerCase();

  // Check if the text field of the message includes the name
  const textMatch = schedule.text?.toLowerCase().includes(lowercaseName);

  if (textMatch) {
    return schedule;
  }

  return null;
};

export const filterMessageSchedules = (
  schedules: ScheduleWithDate[],
  name: string
) => {
  const lowercaseName = name.toLowerCase();
  return schedules
    .map((schedule) => filterMessageSchedule(schedule, lowercaseName))
    .filter(isPresent);
};
